import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { preloadCriticalData } from "./config/api";
import { getPageInfoByPath, updatePageMetadata } from "./data/pageInfo";
import { OrganizationSchema, PersonSchema } from "./composents/StructuredData";
import { AppProvider } from "./context/AppContext";
import { installImageProtection } from "./utils/imageProtection";
import "./styles/main.scss";

import ScrollToTop from "./composents/ScrollToTop.jsx";
import TNav from "./composents/TNav";
import AnimatedHero from "./composents/AnimatedHero";
import Header from "./composents/Header";
import Footer from "./composents/Footer";
import Prestation from "./composents/Prestation";
import AdminAuth from "./composents/AdminAuth";

// Lazy loading des routes pour améliorer les performances
const Realisation = lazy(() => import("./composents/realisation/Realisation"));
const ADecouvrirAussi = lazy(() => import("./composents/ADecouvrirAussi"));
const PageContact = lazy(() => import("./composents/Contact"));
const AProposDeroulement = lazy(() => import("./composents/APropos/AProposDeroulement"));
const AProposPresentation = lazy(() => import("./composents/APropos/AProposPresentation"));
const ProjectManager = lazy(() => import("./composents/ProjectManager.jsx"));
const MentionLegaless = lazy(() => import("./composents/MentionLegales.jsx"));
const ProjetDetailss = lazy(() => import("./composents/ProjetDetails"));
const SiteMap = lazy(() => import("./composents/Sitemap/Sitemap"));
const ServicePage = lazy(() => import("./composents/services/ServicePage"));
const AproposSignature = lazy(() => import("./composents/APropos/AProposSignature.jsx"));
const PrintHome = lazy(() => import("./composents/PrintHome.jsx"));
const Checkout = lazy(() => import("./composents/Checkout.jsx"));
const CheckoutSuccess = lazy(() => import("./composents/CheckoutSuccess.jsx"));

// Composant de chargement pour le Suspense
function LoadingFallback() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: '#0b0f18'
    }}>
      <div style={{
        color: '#fff',
        fontSize: '1.2rem',
        fontWeight: '600'
      }}>
        Chargement...
      </div>
    </div>
  );
}

function AppInner() {
  const location = useLocation();

  // Préchargement des données API en arrière-plan (non bloquant)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      preloadCriticalData().catch(err => {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[API Preload]', err);
        }
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  // Protection des images : bloque le glisser-déposer et le clic droit
  useEffect(() => installImageProtection(), []);

  // Mise à jour des métadonnées
  useEffect(() => {
    const info = getPageInfoByPath(location.pathname);
    updatePageMetadata(info);
  }, [location.pathname]);

  function Accueil() {
    const [shouldRenderPrestation, setShouldRenderPrestation] = useState(false);

    useEffect(() => {
      // Précharger le composant Prestation après le montage initial.
      // Délai augmenté (200ms) pour réduire la surcharge du thread principal
      // et éviter les saccades au moment de l'apparition de l'Observer.
      const timer = setTimeout(() => {
        setShouldRenderPrestation(true);
      }, 200); // <-- MODIFIÉ

      return () => clearTimeout(timer);
    }, []);

    return (
      <main role="main" id="main-content">
        <AnimatedHero />
        <section className="fond-hero" aria-label="Projets récents">
          <Header />
        </section>
        <section
          aria-label="Services photographiques"
          style={{
            // Réserver l'espace pour éviter le jump
            minHeight: shouldRenderPrestation ? 'auto' : '600px'
          }}
        >
          {shouldRenderPrestation && <Prestation />}
        </section>
        
        {/* Avis masqués temporairement (aucun avis pour le moment) */}
        <Footer />
      </main>
    );
  }

  function APropos() {
    return (
      <main role="main">
        <Suspense fallback={<LoadingFallback />}>
          <AProposPresentation />
          <AproposSignature />
          <AProposDeroulement />
          <Footer />
        </Suspense>
      </main>
    );
  }

  function ProjetDetails() {
    return (
      <main role="main">
        <Suspense fallback={<LoadingFallback />}>
          <ProjetDetailss />
        </Suspense>
      </main>
    );
  }

  function RealisationPage() {
    return (
      <main role="main">
        <Suspense fallback={<LoadingFallback />}>
          <Realisation />
          <ADecouvrirAussi />
          <Footer />
        </Suspense>
      </main>
    );
  }

  function SiteMapPage() {
    return (
      <main role="main">
        <Suspense fallback={<LoadingFallback />}>
          <SiteMap />
          <Footer />
        </Suspense>
      </main>
    );
  }

  function MentionLegales() {
    return (
      <main role="main">
        <Suspense fallback={<LoadingFallback />}>
          <MentionLegaless />
          <Footer />
        </Suspense>
      </main>
    );
  }

  function Contact() {
    return (
      <main role="main">
        <Suspense fallback={<LoadingFallback />}>
          <PageContact />
        </Suspense>
      </main>
    );
  }

  function Manager() {
    return (
      <main role="main">
        <AdminAuth>
          <Suspense fallback={<LoadingFallback />}>
            <ProjectManager />
          </Suspense>
        </AdminAuth>
      </main>
    );
  }

  function ServiceRoute({ slug }) {
    return (
      <main role="main">
        <Suspense fallback={<LoadingFallback />}>
          <ServicePage slug={slug} />
          <Footer />
        </Suspense>
      </main>
    );
  }

  return (
    <>
      <OrganizationSchema />
      <PersonSchema />
      <TNav />
      <Routes>
        <Route path="/" element={<Accueil />} />
        <Route path="/a-propos" element={<APropos />} />
        <Route path="/realisations" element={<RealisationPage />} />
        <Route path="/realisation/:id" element={<ProjetDetails />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/sitemap" element={<SiteMapPage />} />
        <Route path="/mentions-legales" element={<MentionLegales />} />
        <Route path="/services/portraits" element={<ServiceRoute slug="portraits" />} />
        <Route path="/print" element={<PrintHome/>} />
        <Route path="/checkout" element={<Checkout/>} />
        <Route path="/checkout/success" element={<CheckoutSuccess/>} />
        <Route path="/services/evenementiel" element={<ServiceRoute slug="evenementiel" />} />
        <Route path="/services/entreprise" element={<ServiceRoute slug="entreprise" />} />
        <Route path="/admin" element={<Manager />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <div className="">
          <ScrollToTop />
          <AppInner />
        </div>
      </BrowserRouter>
    </AppProvider>
  );
}
